import { Typography } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { InfoCard, Stack } from '@superdispatch/ui';
import { Box, Button } from '@superdispatch/ui-lab';
import { Form, FormikProvider } from 'formik';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from 'shared/helpers/RoutingHelpers';
import {
  RatingShipperForm,
  useRatingShipperForm,
} from 'shared/modules/ratings/RatingShipperForm';
import { useShipperProfile } from 'shared/modules/shipper/LoadboardShipperAPI';
import { ShipperProfileDTO } from 'shared/modules/shipper/ShipperProfileDTO';
import { getShipperProfileURL } from 'shared/modules/shipper/ShipperProfileLink';
import { ScrollToTop } from 'shared/routing/ScrollToTop';

interface RateShipperPageContentProps {
  profile: ShipperProfileDTO;
}

function RateShipperPageContent({ profile }: RateShipperPageContentProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useQuery();

  const { formik, isEditingRating } = useRatingShipperForm({
    guid: profile.guid,
    onSuccess() {
      const isFromRatingsToGive = query.utm_medium === 'Ratings To Give';
      const ratingsToGivePath = {
        pathname: '/ratings',
        search: location.search,
      };

      navigate(
        isFromRatingsToGive
          ? ratingsToGivePath
          : getShipperProfileURL({
              guid: profile.guid,
              medium: 'Shipper Profile',
              tab: 'ratings',
            }),
        { replace: true },
      );
    },
  });

  useEffect(() => {
    if (query.open_rating_dialog) {
      setQuery({ open_rating_dialog: undefined });
    }
  }, [query, setQuery]);

  return (
    <>
      <ScrollToTop />

      <FormikProvider value={formik}>
        <Form>
          <Box padding="xsmall">
            <InfoCard>
              <Stack>
                <Typography variant="h3">{profile.name}</Typography>

                <RatingShipperForm />
              </Stack>

              <Box paddingTop="medium">
                <Button
                  type="submit"
                  pending={formik.isSubmitting}
                  disabled={!formik.values.rating || !formik.dirty}
                  fullWidth={true}
                >
                  {isEditingRating ? 'Update' : 'Submit'}
                </Button>
              </Box>
            </InfoCard>
          </Box>
        </Form>
      </FormikProvider>
    </>
  );
}

export function RateShipperPage({ guid }: { guid: string }) {
  const { data: profile } = useShipperProfile(guid);

  if (!profile) {
    return (
      <Box padding="xsmall">
        <InfoCard>
          <Stack>
            <Skeleton width="80%" height="36px" />

            <Skeleton />
          </Stack>
        </InfoCard>
      </Box>
    );
  }

  return <RateShipperPageContent profile={profile} />;
}
